/* ============================================================
   HL — Influencer Videos ("Worn by you")

   Runs the carousel built by sections/influencer-videos.liquid.
   Deliberately independent from product-videos.js: that file owns the
   product video block and is not touched here, so a change to one
   carousel can never regress the other. Both share the vendored
   Swiper 11.2.10 in assets/, whose URL arrives on [data-swiper-src]
   because a plain asset cannot call asset_url.

   Handles:
     - native <video> (Shopify file, Dropbox, direct MP4): muted
       autoplay in view, tap to pause, separate sound toggle
     - Dropbox links whose token has rotated: falls back to the
       Dropbox viewer in an iframe instead of showing a black box
     - iframe facades (YouTube / Vimeo / Instagram / TikTok): the
       poster is swapped for the real player on click
   ============================================================ */
(() => {
  const SECTION_SELECTOR = '.hl-ivs';
  const POSTER_FRAGMENT = '#t=0.1';
  const TAP_SLOP = 10;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Only the host is rewritten. The signature on a modern /scl/fi/ link lives
     in st= and rlkey, and appending raw=1 invalidates it (403). Every query
     param is left untouched — including dl=0, which dl.dropboxusercontent.com
     serves inline anyway. This matches the live product video player. */
  const normalizeDropbox = (raw) => {
    if (!raw || raw.indexOf('dropbox.com') === -1) return raw;
    try {
      const url = new URL(raw, window.location.href);
      url.hostname = 'dl.dropboxusercontent.com';
      if (!url.hash) url.hash = POSTER_FRAGMENT.slice(1);
      return url.toString();
    } catch (err) {
      return raw;
    }
  };

  /* Swiper may already be loading because the product video block asked for
     the same file, so wait for the global instead of racing a second copy. */
  const ensureSwiper = (src, cb) => {
    if (window.Swiper) return cb();

    const waitFor = () => {
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (window.Swiper) {
          clearInterval(timer);
          cb();
        } else if (tries > 100) {
          clearInterval(timer);
          console.warn('[hl-ivs] Swiper never became available');
        }
      }, 50);
    };

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', cb, { once: true });
      waitFor();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => cb(), { once: true });
    script.addEventListener('error', () =>
      console.warn('[hl-ivs] could not load Swiper from', src)
    );
    document.body.appendChild(script);
  };

  const buildSwiper = (root, centerMode) => {
    const swiperEl = root.querySelector('.swiper');
    if (!swiperEl) return null;

    const slideCount = root.querySelectorAll('.swiper-slide').length;
    const perDesktop = parseFloat(root.dataset.slidesDesktop) || 4;
    const perTablet = parseFloat(root.dataset.slidesTablet) || 3;
    const perMobile = parseFloat(root.dataset.slidesMobile) || 1.2;
    const gap = parseInt(getComputedStyle(root).getPropertyValue('--ivs-gap'), 10) || 16;
    const multi = slideCount > 1;
    const paginationEl = root.querySelector('.swiper-pagination');

    /* Swiper needs more slides than fit on screen to have anything to wrap
       around with. One spare card either side is enough in practice — asking
       for double the row was stricter than the library requires and quietly
       disabled looping on perfectly good sets. */
    const loopMode = root.dataset.loopMode || 'infinite';
    const widest = Math.ceil(Math.max(perDesktop, perTablet, perMobile));
    const canLoop = slideCount > widest && slideCount > 2;
    const useLoop = loopMode === 'infinite' && canLoop && multi;
    const useRewind = multi && !useLoop && loopMode !== 'off';

    return new Swiper(swiperEl, {
      slidesPerView: perMobile,
      spaceBetween: gap,
      grabCursor: multi,
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
      loop: useLoop,
      /* One spare either side is enough to keep the wrap-around card painted;
         asking for a whole row starves the loop when the set is short. */
      loopAdditionalSlides: useLoop ? 1 : 0,
      rewind: useRewind,
      /* Centred mode needs a real middle slide, and starting on it means the
         carousel opens balanced instead of hard against its left edge. */
      centeredSlides: centerMode,
      /* Looping already starts mid-track, so the manual offset is only needed
         when the carousel has real ends. */
      initialSlide: centerMode && !useLoop ? Math.floor((slideCount - 1) / 2) : 0,
      navigation: multi
        ? {
            nextEl: root.querySelector('.swiper-button-next'),
            prevEl: root.querySelector('.swiper-button-prev'),
          }
        : false,
      pagination:
        multi && paginationEl ? { el: paginationEl, clickable: true } : false,
      breakpoints: {
        768: { slidesPerView: Math.min(perTablet, slideCount), spaceBetween: gap },
        1024: { slidesPerView: Math.min(perDesktop, slideCount), spaceBetween: gap + 4 },
      },
      /* Swiper fires a click at the end of a drag; without these, finishing a
         swipe on a card also toggled that card's video. */
      preventClicks: true,
      preventClicksPropagation: true,
      touchStartPreventDefault: false,
      threshold: 6,
      touchAngle: 35,
      touchReleaseOnEdges: true,
    });
  };

  /* Swiper's loop has nothing to wrap around with unless there are clearly
     more slides than fit on screen — below that it disables looping without
     saying so. Repeating the set is what makes a short list feel endless:
     after the last card the first one comes round again instead of a gap. */
  const ensureLoopCapacity = (root) => {
    if ((root.dataset.loopMode || 'infinite') !== 'infinite') return;
    /* Opt-out: some sets are better left short and finite than shown twice. */
    if (root.dataset.loopRepeat === 'false') return;

    const wrapper = root.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const originals = Array.from(wrapper.children);
    if (originals.length < 2) return;

    const widest = Math.ceil(
      Math.max(
        parseFloat(root.dataset.slidesDesktop) || 4,
        parseFloat(root.dataset.slidesTablet) || 3,
        parseFloat(root.dataset.slidesMobile) || 1.2
      )
    );
    const needed = widest * 2 + 1;
    if (originals.length >= needed) return;

    let passes = 0;
    while (wrapper.children.length < needed && passes < 4) {
      originals.forEach((slide) => wrapper.appendChild(slide.cloneNode(true)));
      passes += 1;
    }
  };

  const initInstance = (root) => {
    if (root.dataset.ivsReady === 'true') return;
    root.dataset.ivsReady = 'true';

    const centerMode = root.dataset.playbackMode !== 'manual';
    const autoplayEnabled =
      root.dataset.autoplayInView !== 'false' && !prefersReducedMotion();

    /* Runs before anything is wired so the copies get the same listeners. */
    ensureLoopCapacity(root);

    const videos = Array.from(root.querySelectorAll('.hl-ivs__media--video video'));

    videos.forEach((video) => {
      const current = video.getAttribute('src') || '';
      const fixed = normalizeDropbox(current);
      if (fixed !== current) video.setAttribute('src', fixed);

      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      primePosterFrame(video);
      attachDropboxFallback(video);
    });

    const swiper = buildSwiper(root, centerMode);
    const ctx = { centerMode, swiper };

    wireFacades(root, ctx);
    wirePlayState(videos);
    wireVideoTaps(root, videos, ctx);
    wireImageTaps(root, ctx);
    wireSoundToggles(root, ctx);

    if (swiper) {
      const refreshOffset = () => applyActiveOffset(root, swiper);
      swiper.on('slideChange', refreshOffset);
      swiper.on('resize', refreshOffset);
      /* Cards are sized by CSS aspect-ratio, so the width is only reliable
         once layout has settled. */
      window.setTimeout(refreshOffset, 60);
    }

    if (centerMode) {
      wireNudges(root, swiper, true);
      wireCenterPlayback(root, videos, swiper);
    } else {
      wireNudges(root, swiper, false);
      if (autoplayEnabled) wireAutoplay(root, videos);
    }
  };

  /* Cross-origin players can't be driven through the DOM, but both Vimeo and
     YouTube accept commands over postMessage without loading their SDKs. This
     is what lets the carousel own playback instead of every embed autoplaying
     itself. */
  const frameCommand = (iframe, action) => {
    if (!iframe || !iframe.contentWindow) return;
    const wrapper = iframe.closest('[data-provider]');
    const kind = wrapper ? wrapper.dataset.provider : '';

    try {
      if (kind === 'vimeo') {
        const send = (payload) =>
          iframe.contentWindow.postMessage(
            JSON.stringify(payload),
            'https://player.vimeo.com'
          );
        if (action === 'play') send({ method: 'play' });
        else if (action === 'pause') send({ method: 'pause' });
        else if (action === 'unmute') {
          /* setMuted alone is ignored by older player builds, so the volume
             is set too — whichever the build understands wins. */
          send({ method: 'setMuted', value: false });
          send({ method: 'setVolume', value: 1 });
        } else if (action === 'mute') {
          send({ method: 'setMuted', value: true });
          send({ method: 'setVolume', value: 0 });
        }
      } else if (kind === 'youtube') {
        const send = (func, args) =>
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: func, args: args || [] }),
            '*'
          );
        if (action === 'play') send('playVideo');
        else if (action === 'pause') send('pauseVideo');
        else if (action === 'unmute') {
          send('unMute');
          send('setVolume', [100]);
        } else if (action === 'mute') send('mute');
      }
      /* Instagram and TikTok expose no such API — their embeds simply stay
         paused until the visitor presses their own play button. */
    } catch (err) {
      /* Frame not ready yet; the next slideChange will retry. */
    }
  };

  /* Embed state can't be read back from a cross-origin frame, so it is
     tracked here — every command goes through these helpers so the buttons
     and the frame never disagree. */
  const setFramePlaying = (frame, playing) => {
    const wrapper = frame.closest('.hl-ivs__media');
    if (!wrapper) return;
    wrapper.classList.toggle('hl-ivs__media--playing', playing);
    frame.dataset.ivsPlaying = playing ? 'true' : 'false';
  };

  const setFrameMuted = (frame, muted) => {
    frameCommand(frame, muted ? 'mute' : 'unmute');
    frame.dataset.ivsMuted = muted ? 'true' : 'false';
    const wrapper = frame.closest('.hl-ivs__media');
    const button = wrapper && wrapper.querySelector('.hl-ivs__sound');
    if (button) button.setAttribute('aria-pressed', muted ? 'false' : 'true');
  };

  const playFrame = (frame) => {
    frameCommand(frame, 'play');
    setFramePlaying(frame, true);
  };

  const pauseFrame = (frame) => {
    frameCommand(frame, 'pause');
    setFramePlaying(frame, false);
  };

  /* Only one thing makes noise at a time, across both kinds of player. */
  const soloAudio = (root, keepFrame, keepVideo) => {
    root.querySelectorAll('.hl-ivs__media--video video').forEach((video) => {
      if (video !== keepVideo && !video.muted) {
        video.muted = true;
        syncSoundButton(video);
      }
    });
    root.querySelectorAll('.hl-ivs__media--iframe iframe').forEach((frame) => {
      if (frame !== keepFrame && frame.dataset.ivsMuted !== 'true') {
        setFrameMuted(frame, true);
      }
    });
  };

  /* The centre card is scaled up, and a scaled element grows about its own
     centre — so it eats half its extra width out of the gap on each side and
     the spacing stops looking even. Pushing everything to its left and right
     outwards by that same amount restores a single uniform gap. */
  const applyActiveOffset = (root, swiper) => {
    if (!swiper || !swiper.slides) return;

    const scale = parseFloat(root.dataset.activeScale) || 1;
    const activeIndex = swiper.activeIndex;
    const activeSlide = swiper.slides[activeIndex];
    if (!activeSlide) return;

    const card = activeSlide.querySelector('.hl-ivs__card');
    const width = card ? card.offsetWidth : 0;
    const shift = scale > 1 && width ? ((scale - 1) * width) / 2 : 0;

    swiper.slides.forEach((slide, index) => {
      if (index === activeIndex || !shift) {
        slide.style.transform = '';
      } else if (index < activeIndex) {
        slide.style.transform = `translateX(${-shift}px)`;
      } else {
        slide.style.transform = `translateX(${shift}px)`;
      }
    });
  };
  /* Every way of picking an off-centre card funnels through here, so images,
     videos, embeds and the sound button all behave the same way. */
  const centreSlide = (ctx, slide) => {
    if (!ctx || !ctx.centerMode || !ctx.swiper || !slide) return false;
    if (slide.classList.contains('swiper-slide-active')) return false;
    const index = Array.prototype.indexOf.call(slide.parentElement.children, slide);
    if (index < 0) return false;
    ctx.swiper.slideTo(index);
    return true;
  };

  function wireNudges(root, swiper, centerMode) {
    root.querySelectorAll('.hl-ivs__nudge').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const slide = button.closest('.swiper-slide');
        const frame = button.closest('.hl-ivs__media').querySelector('iframe');
        if (!frame) return;

        /* Off-centre cards are not play buttons: tapping one brings it to the
           middle, and the sync that follows starts it. */
        if (centreSlide({ centerMode: centerMode, swiper: swiper }, slide)) return;

        if (frame.dataset.ivsPlaying === 'true') {
          pauseFrame(frame);
        } else {
          playFrame(frame);
        }
      });
    });
  }

  /* Centred mode: exactly one clip runs at a time — the one in the middle.
     Moving the carousel is what changes which video plays, so the arrows
     become the playback control rather than a pure scroll affordance. */
  function wireCenterPlayback(root, videos, swiper) {
    if (!swiper) return;

    const frames = Array.from(root.querySelectorAll('.hl-ivs__media--iframe iframe'));

    const sync = () => {
      const activeSlide = swiper.slides && swiper.slides[swiper.activeIndex];

      videos.forEach((video) => {
        const isCentre = activeSlide && activeSlide.contains(video);
        if (isCentre) {
          if (video.paused) {
            video.muted = true;
            syncSoundButton(video);
            video.play().catch(() => {});
          }
        } else if (!video.paused || video.currentTime > 0) {
          video.pause();
          video.currentTime = 0;
          video.muted = true;
          syncSoundButton(video);
        }
      });

      frames.forEach((frame) => {
        const isCentre = activeSlide && activeSlide.contains(frame);
        if (isCentre) {
          playFrame(frame);
        } else {
          pauseFrame(frame);
          if (frame.dataset.ivsMuted !== 'true') setFrameMuted(frame, true);
        }
      });
    };

    swiper.on('slideChange', sync);
    swiper.on('transitionEnd', sync);

    /* Embeds report ready asynchronously and ignore anything sent before
       that, so replay the sync once they have had time to boot. */
    if (frames.length) {
      setTimeout(sync, 1200);
      setTimeout(sync, 3000);
    }

    /* Wait for the section to be on screen before the first clip starts:
       a video playing above the fold while the visitor is elsewhere on the
       page is wasted bandwidth. */
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              sync();
            } else {
              videos.forEach((video) => {
                if (!video.paused) {
                  video.pause();
                  video.muted = true;
                  syncSoundButton(video);
                }
              });
              frames.forEach((frame) => pauseFrame(frame));
            }
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(root);
    } else {
      sync();
    }
  }

  /* iOS reports metadata without decoding a frame, so nudge it to the
     fragment time once dimensions are known. readyState >= 2 means a frame
     is already painted and nothing is needed. */
  function primePosterFrame(video) {
    const seek = () => {
      if (video.dataset.posterPrimed === 'true' || video.readyState >= 2) return;
      video.dataset.posterPrimed = 'true';
      try {
        video.currentTime = 0.1;
      } catch (err) {
        /* Some browsers refuse to seek before enough data arrives. Harmless:
           the poster attribute still covers the frame. */
      }
    };

    video.addEventListener('loadedmetadata', seek);
    if (video.readyState >= 1) seek();
  }

  /* A Dropbox direct link can stop resolving when the share token rotates —
     this has already produced black players on HL. When the media element
     errors, swap it for the Dropbox viewer iframe, which follows the
     redirect itself. */
  function attachDropboxFallback(video) {
    const wrapper = video.closest('.hl-ivs__media');
    if (!wrapper) return;
    const fallback = wrapper.dataset.dropboxFallback;
    if (!fallback) return;

    const swap = () => {
      if (wrapper.dataset.ivsFallbackApplied === 'true') return;
      wrapper.dataset.ivsFallbackApplied = 'true';

      const iframe = document.createElement('iframe');
      iframe.src = fallback;
      iframe.loading = 'lazy';
      iframe.setAttribute('allow', 'autoplay; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('scrolling', 'no');
      iframe.title = 'Video';

      video.remove();
      const sound = wrapper.querySelector('.hl-ivs__sound');
      if (sound) sound.remove();
      wrapper.classList.remove('hl-ivs__media--video');
      wrapper.classList.add('hl-ivs__media--iframe', 'hl-ivs__media--embed-native');
      wrapper.dataset.kind = 'iframe';
      wrapper.appendChild(iframe);
      console.warn('[hl-ivs] Dropbox direct link failed, using viewer iframe');
    };

    video.addEventListener('error', swap);
    /* An errored <source>-less video reports networkState 3 with no error
       event in some Safari builds, so check once metadata should have landed. */
    setTimeout(() => {
      if (video.readyState === 0 && video.networkState === 3) swap();
    }, 6000);
  }

  /* The facade keeps third-party players out of the initial page load: six
     embeds on a product page is a real LCP cost. Click swaps in the iframe. */
  function wireFacades(root, ctx) {
    root.querySelectorAll('.hl-ivs__facade').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        /* Same rule as every other card: off-centre means centre it first. */
        if (centreSlide(ctx, button.closest('.swiper-slide'))) return;

        const wrapper = button.closest('.hl-ivs__media');
        const src = button.dataset.embedSrc;
        if (!wrapper || !src) return;

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.setAttribute(
          'allow',
          'autoplay; encrypted-media; picture-in-picture; fullscreen'
        );
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('scrolling', 'no');
        iframe.title = button.getAttribute('aria-label') || 'Video';

        wrapper.innerHTML = '';
        wrapper.appendChild(iframe);
      });
    });
  }

  /* A tap toggles playback, a swipe must not: track finger travel and ignore
     anything that moved. */
  function wireVideoTaps(root, videos, ctx) {
    root.querySelectorAll('.hl-ivs__media--video').forEach((wrapper) => {
      let startX = 0;
      let startY = 0;
      let dragged = false;

      wrapper.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          dragged = false;
        },
        { passive: true }
      );

      wrapper.addEventListener(
        'touchmove',
        (event) => {
          const touch = event.changedTouches[0];
          if (
            Math.abs(touch.clientX - startX) > TAP_SLOP ||
            Math.abs(touch.clientY - startY) > TAP_SLOP
          ) {
            dragged = true;
          }
        },
        { passive: true }
      );

      wrapper.addEventListener('touchend', (event) => {
        if (dragged) return;
        handleTap(wrapper, videos, event, ctx);
      });

      wrapper.addEventListener('click', (event) =>
        handleTap(wrapper, videos, event, ctx)
      );
    });
  }

  /* A photo entry is a card like any other: clicking it should move the
     carousel, not sit there doing nothing. */
  function wireImageTaps(root, ctx) {
    root.querySelectorAll('.hl-ivs__media--image').forEach((wrapper) => {
      wrapper.addEventListener('click', (event) => {
        if (centreSlide(ctx, wrapper.closest('.swiper-slide'))) {
          event.preventDefault();
          event.stopPropagation();
        }
      });
    });
  }

  function handleTap(wrapper, videos, event, ctx) {
    if (event.target.closest('.hl-ivs__sound')) return;
    event.preventDefault();
    event.stopPropagation();

    const video = wrapper.querySelector('video');
    if (!video) return;

    /* In centred mode a side card is not a play button — tapping it brings
       that clip to the middle, which is what starts it. */
    if (ctx && ctx.centerMode && ctx.swiper) {
      if (centreSlide(ctx, wrapper.closest('.swiper-slide'))) return;
      if (video.paused) {
        playWithSound(video);
      } else {
        video.pause();
      }
      return;
    }

    if (video.paused) {
      /* Choosing a card is an explicit act, so it takes over: every other
         clip stops. This holds at all widths now that autoplay is off by
         default — two clips talking over each other is never wanted. */
      videos.forEach((other) => {
        if (other !== video) {
          other.pause();
          other.currentTime = 0;
          other.muted = true;
          syncSoundButton(other);
        }
      });
      playWithSound(video);
    } else {
      video.pause();
    }
  }

  /* A deliberate play should be audible. Autoplay policies and Low Power
     Mode can still refuse it, so fall back to muted rather than leaving the
     card frozen. */
  function playWithSound(video) {
    video.muted = false;
    syncSoundButton(video);
    video.play().catch(() => {
      video.muted = true;
      syncSoundButton(video);
      video.play().catch(() => {});
    });
  }

  /* The play badge is driven by the media events, not by the click handler,
     so it stays correct when playback ends or is paused from elsewhere. */
  function wirePlayState(videos) {
    videos.forEach((video) => {
      const wrapper = video.closest('.hl-ivs__media');
      const card = video.closest('.hl-ivs__card');
      if (!wrapper) return;
      const setPlaying = () => {
        wrapper.classList.add('hl-ivs__media--playing');
        if (card) card.classList.add('hl-ivs__card--playing');
      };
      const setPaused = () => {
        wrapper.classList.remove('hl-ivs__media--playing');
        if (card) card.classList.remove('hl-ivs__card--playing');
      };
      video.addEventListener('play', setPlaying);
      video.addEventListener('playing', setPlaying);
      video.addEventListener('pause', setPaused);
      video.addEventListener('ended', setPaused);
    });
  }

  /* Sound is opt-in and lives on its own control: unmuting on tap surprised
     people scrolling a product page. Audible playback can still be refused
     (Low Power Mode), so fall back to muted rather than stalling. */
  function wireSoundToggles(root, ctx) {
    root.querySelectorAll('.hl-ivs__sound').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        /* Unmuting an off-centre card used to start a second soundtrack over
           the one already playing. In centred mode the sound button now means
           "I want to watch this one": it centres the card, and the sync that
           follows hands it playback. The unmute waits for the slide to land,
           otherwise that same sync would immediately mute it again. */
        const ownSlide = button.closest('.swiper-slide');
        if (centreSlide(ctx, ownSlide)) {
          window.setTimeout(() => {
            const media = button.closest('.hl-ivs__media');
            const frame = media && media.querySelector('iframe');
            const video = media && media.querySelector('video');
            if (frame) {
              soloAudio(root, frame, null);
              setFrameMuted(frame, false);
            } else if (video) {
              soloAudio(root, null, video);
              video.muted = false;
              syncSoundButton(video);
              video.play().catch(() => {});
            }
          }, 450);
          return;
        }

        const wrapper = button.closest('.hl-ivs__media');
        const frame = wrapper && wrapper.querySelector('iframe');

        /* Embeds take the same path, just through postMessage instead of the
           media element. */
        if (frame) {
          const wasMuted = frame.dataset.ivsMuted !== 'false';
          if (wasMuted) {
            soloAudio(root, frame, null);
            setFrameMuted(frame, false);
            if (frame.dataset.ivsPlaying !== 'true') playFrame(frame);
          } else {
            setFrameMuted(frame, true);
          }
          return;
        }

        const video = wrapper && wrapper.querySelector('video');
        if (!video) return;

        if (video.muted) {
          /* One at a time: unmuting this card silences every other one. */
          soloAudio(root, null, video);
          video.muted = false;
          video.play().catch(() => {
            video.muted = true;
            video.play().catch(() => {});
            syncSoundButton(video);
          });
        } else {
          video.muted = true;
        }
        syncSoundButton(video);
      });
    });
  }

  function syncSoundButton(video) {
    const wrapper = video.closest('.hl-ivs__media');
    const button = wrapper && wrapper.querySelector('.hl-ivs__sound');
    if (button) button.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
  }

  /* Autoplay only applies to native video. YouTube, Vimeo, Instagram and
     TikTok cannot be started or muted from this page. */
  function wireAutoplay(root, videos) {
    if (!videos.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (video.paused) {
              video.muted = true;
              syncSoundButton(video);
              video.play().catch(() => {});
            }
          } else if (!video.paused) {
            video.pause();
            /* Leave currentTime alone: scrolling back should resume where the
               clip was, not restart it. */
            video.muted = true;
            syncSoundButton(video);
          }
        });
      },
      { threshold: 0.6 }
    );

    videos.forEach((video) => observer.observe(video));
  }

  const bootstrap = () => {
    const roots = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!roots.length) return;

    const swiperSrc =
      roots[0].dataset.swiperSrc ||
      'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

    ensureSwiper(swiperSrc, () => roots.forEach(initInstance));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* The theme editor re-renders a section into a fresh DOM node, which drops
     the Swiper instance and every listener with it. Re-init the new node
     instead of leaving a dead carousel behind. */
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR)
      : null;
    if (!root) return;
    root.dataset.ivsReady = 'false';
    const swiperSrc = root.dataset.swiperSrc;
    ensureSwiper(swiperSrc, () => initInstance(root));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR)
      : null;
    if (root) root.dataset.ivsReady = 'false';
  });
})();
